import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/login/session';
import { Avatar, Card, Chip, EmptyState } from '@/shared/ui/atoms';
import { accountApi } from '@/features/settings/api';
import type { AccountTeam, ConsoleAccount, ConsoleMenu } from '@/features/settings/types';
import { MenuPicker } from '@/features/settings/views/MenuPicker';

const INPUT = 'rounded-md border border-line bg-surface px-2.5 py-1.5 text-[13px]';
const PRIMARY =
  'cursor-pointer rounded-md bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40';
const TH =
  'bg-canvas [&>th]:border-y [&>th]:border-line [&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted';
// 셀 높이가 제각각이다(이름+이메일 두 줄, 셀렉트, 칩 한 줄) — 위로 붙이면 한 줄짜리 칩이 행 위쪽에 떠 보인다.
const TR =
  'border-b border-line-soft last:border-0 hover:bg-canvas/60 [&>td]:px-3 [&>td]:py-2.5 [&>td]:align-middle [&>td]:text-[12.5px]';
const FORM = 'flex flex-col gap-2.5 rounded-md border border-line-soft bg-canvas p-3';
const LINK = 'cursor-pointer text-[11.5px] hover:underline';

type Tab = 'accounts' | 'teams';
const TABS: ReadonlyArray<readonly [Tab, string]> = [
  ['accounts', '계정'],
  ['teams', '팀'],
];

/**
 * 계정·팀·메뉴 권한 관리. 계정의 최종 권한은 팀 기본 권한 ∪ 개별 권한이다 — 팀 기본 권한을 바꾸면
 * 소속 계정 전원에게 바로 반영되고, 개별 권한은 그 위에 더하기만 한다.
 *
 * 로그인 강제(`CONSOLE_LOGIN_REQUIRED`)가 켜져 있을 때만 이 권한이 실제로 메뉴와 API를 막는다.
 */
export function AccountView(): React.JSX.Element {
  const [menus, setMenus] = useState<ConsoleMenu[]>([]);
  const [teams, setTeams] = useState<AccountTeam[] | null>(null);
  const [accounts, setAccounts] = useState<ConsoleAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { session } = useSession();
  // 탭을 주소에 둔다 — 팀 권한을 고치다 새로고침해도 팀 탭에 머문다.
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'teams' ? 'teams' : 'accounts';
  const count = { accounts: accounts?.length, teams: teams?.length };

  const load = useCallback(() => {
    // 팀 기본 권한이 바뀌면 계정의 최종 권한도 바뀌므로 둘을 같이 다시 읽는다.
    Promise.all([accountApi.teams(), accountApi.list()])
      .then(([teamPayload, accountPayload]) => {
        setTeams(teamPayload.teams);
        setAccounts(accountPayload.accounts);
        setError(null);
      })
      .catch((cause: Error) => setError(cause.message));
  }, []);

  useEffect(load, [load]);
  useEffect(() => {
    accountApi
      .menus()
      .then((payload) => setMenus(payload.menus))
      .catch((cause: Error) => setError(cause.message));
  }, []);

  const run = useCallback(
    (action: Promise<unknown>) =>
      action.then(load).catch((cause: Error) => setError(cause.message)),
    [load],
  );

  return (
    <div className="flex flex-col gap-3">
      {session && !session.required && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          로그인 강제가 꺼져 있어 여기서 정한 권한은 저장만 된다 — 메뉴 접근을 막지는 않는다. 모두 비밀번호를 정한 뒤 켠다.
        </div>
      )}
      {error && <EmptyState message={`실패 — ${error}`} />}
      <div role="tablist" aria-label="계정과 팀" className="flex items-end gap-5 border-b border-line">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setParams(key === 'accounts' ? {} : { tab: key }, { replace: true })}
            className={`-mb-px border-b-2 px-1 pt-1 pb-2 text-sm transition-colors ${
              tab === key ? 'border-accent font-bold text-accent' : 'border-transparent font-medium text-muted hover:text-ink'
            }`}
          >
            {label}
            {count[key] !== undefined && <span className="ml-1.5 text-xs font-medium text-faint">{count[key]}</span>}
          </button>
        ))}
      </div>
      {tab === 'teams' ? (
        <TeamCard menus={menus} teams={teams} onRun={run} />
      ) : (
        <AccountCard menus={menus} teams={teams ?? []} accounts={accounts} onRun={run} />
      )}
    </div>
  );
}

type Run = (action: Promise<unknown>) => Promise<unknown>;

function TeamCard({ menus, teams, onRun }: { menus: ConsoleMenu[]; teams: AccountTeam[] | null; onRun: Run }): React.JSX.Element {
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  // 편집 중 체크는 여기에만 모은다 — 저장을 눌러야 반영되고, 취소하면 버린다.
  const [draft, setDraft] = useState<string[]>([]);

  const remove = (team: AccountTeam): void => {
    if (!window.confirm(`팀 "${team.name}"을 지운다. 소속 계정이 남아 있으면 지워지지 않는다.`)) return;
    void onRun(accountApi.removeTeam(team.id));
  };

  return (
    <Card title="팀" hint="팀에 소속된 계정은 팀 기본 권한을 개별 설정 없이 받는다">
      <div className={FORM}>
        <input value={name} onChange={(event) => setName(event.target.value)} placeholder="팀 이름 (예: 백엔드)" className={`${INPUT} w-full`} />
        <MenuPicker menus={menus} picked={picked} onChange={setPicked} />
        <div className="flex justify-end">
          <button
            type="button"
            disabled={name.trim() === ''}
            className={PRIMARY}
            onClick={() => {
              void onRun(accountApi.createTeam({ name: name.trim(), defaultMenus: picked }));
              setName('');
              setPicked([]);
            }}
          >
            팀 추가
          </button>
        </div>
      </div>

      <div className="mt-3">
        {!teams ? (
          <EmptyState message="팀을 불러오는 중…" />
        ) : teams.length === 0 ? (
          <EmptyState message="등록된 팀이 없다." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className={TH}>
                <th className="w-[160px]">팀</th>
                <th>기본 권한</th>
                <th className="w-[120px]" />
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.id} className={TR}>
                  <td className="font-semibold">{team.name}</td>
                  <td>
                    {editing === team.id ? (
                      <MenuPicker menus={menus} picked={draft} onChange={setDraft} />
                    ) : (
                      <MenuChips menus={menus} keys={team.defaultMenus} />
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {editing === team.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            void onRun(accountApi.updateTeam(team.id, { defaultMenus: draft }));
                            setEditing(null);
                          }}
                          className={`${LINK} font-semibold text-accent`}
                        >
                          저장
                        </button>
                        <button type="button" onClick={() => setEditing(null)} className={`ml-3 ${LINK} text-muted`}>
                          취소
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setDraft(team.defaultMenus);
                          setEditing(team.id);
                        }}
                        className={`${LINK} text-accent`}
                      >
                        권한 편집
                      </button>
                    )}
                    <button type="button" onClick={() => remove(team)} className={`ml-3 ${LINK} text-rose-700`}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

function AccountCard({
  menus,
  teams,
  accounts,
  onRun,
}: {
  menus: ConsoleMenu[];
  teams: AccountTeam[];
  accounts: ConsoleAccount[] | null;
  onRun: Run;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [teamId, setTeamId] = useState<number | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  // 편집 중 체크는 여기에만 모은다 — 저장을 눌러야 반영되고, 취소하면 버린다.
  const [draft, setDraft] = useState<string[]>([]);

  const teamDefaults = (id: number | null): string[] => teams.find((team) => team.id === id)?.defaultMenus ?? [];
  // 보낸 결과를 그 줄 버튼 자리에 잠깐 보여 준다 — 목록 전체를 다시 읽을 일이 아니다.
  const [setupSent, setSetupSent] = useState<Record<number, string>>({});
  const sendPasswordSetup = (account: ConsoleAccount): void => {
    setSetupSent((prev) => ({ ...prev, [account.id]: '보내는 중…' }));
    accountApi
      .sendPasswordSetup(account.id)
      .then(() => setSetupSent((prev) => ({ ...prev, [account.id]: '메일 보냄' })))
      .catch((cause: Error) => setSetupSent((prev) => ({ ...prev, [account.id]: `실패 — ${cause.message.replace(/^요청 실패 \(HTTP \d+\) — /, '')}` })));
  };

  return (
    <Card title="계정" hint="최종 권한 = 팀 기본 권한 + 개별 권한. 비활성 계정은 아무 메뉴도 열 수 없다">
      <div className={FORM}>
        <div className="flex gap-2">
          <input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="이메일" className={`${INPUT} flex-1`} />
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="이름" className={`${INPUT} w-[160px]`} />
          <TeamSelect teams={teams} value={teamId} onChange={setTeamId} emptyLabel="팀 선택" />
        </div>
        <MenuPicker menus={menus} picked={picked} onChange={setPicked} locked={teamDefaults(teamId)} />
        <div className="flex justify-end">
          <button
            type="button"
            disabled={email.trim() === '' || name.trim() === '' || teamId === null}
            className={PRIMARY}
            onClick={() => {
              void onRun(accountApi.create({ email: email.trim(), name: name.trim(), teamId, menus: picked }));
              setEmail('');
              setName('');
              setTeamId(null);
              setPicked([]);
            }}
          >
            계정 등록
          </button>
        </div>
      </div>

      <div className="mt-3">
        {!accounts ? (
          <EmptyState message="계정을 불러오는 중…" />
        ) : accounts.length === 0 ? (
          <EmptyState message="등록된 계정이 없다." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className={TH}>
                <th className="w-[240px]">계정</th>
                <th className="w-[150px]">팀</th>
                <th>최종 권한</th>
                <th className="w-[230px]" />
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id} className={`${TR} ${account.active ? '' : 'opacity-60'}`}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      <Avatar name={account.name} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`truncate font-semibold ${account.active ? '' : 'line-through'}`}>{account.name}</span>
                          {!account.hasPassword && <Chip tone="warn">비밀번호 미설정</Chip>}
                        </div>
                        <div className="truncate text-[11px] text-muted">{account.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <TeamSelect
                      teams={teams}
                      value={account.teamId}
                      onChange={(next) => void onRun(accountApi.update(account.id, { teamId: next }))}
                    />
                  </td>
                  <td>
                    {editing === account.id ? (
                      <MenuPicker menus={menus} picked={draft} locked={teamDefaults(account.teamId)} onChange={setDraft} />
                    ) : account.active ? (
                      <MenuChips menus={menus} keys={account.effectiveMenus} />
                    ) : (
                      <Chip tone="warn">비활성</Chip>
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {editing === account.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            void onRun(accountApi.update(account.id, { menus: draft }));
                            setEditing(null);
                          }}
                          className={`${LINK} font-semibold text-accent`}
                        >
                          저장
                        </button>
                        <button type="button" onClick={() => setEditing(null)} className={`ml-3 ${LINK} text-muted`}>
                          취소
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setDraft(account.menus);
                          setEditing(account.id);
                        }}
                        className={`${LINK} text-accent`}
                      >
                        개별 권한
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={!account.active}
                      onClick={() => sendPasswordSetup(account)}
                      className={`ml-3 ${LINK} text-accent disabled:cursor-not-allowed disabled:text-faint disabled:no-underline`}
                    >
                      {setupSent[account.id] ?? (account.hasPassword ? '재설정 메일' : '설정 메일')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onRun(accountApi.update(account.id, { active: !account.active }))}
                      className={`ml-3 ${LINK} text-muted`}
                    >
                      {account.active ? '비활성화' : '활성화'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

function TeamSelect({
  teams,
  value,
  onChange,
  emptyLabel = '팀 없음',
}: {
  teams: AccountTeam[];
  value: number | null;
  onChange: (next: number | null) => void;
  emptyLabel?: string;
}): React.JSX.Element {
  return (
    <select
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      className={`${INPUT} w-[140px]`}
    >
      <option value="">{emptyLabel}</option>
      {teams.map((team) => (
        <option key={team.id} value={team.id}>
          {team.name}
        </option>
      ))}
    </select>
  );
}

/** 카탈로그에 없는 키는 원문으로 보여 준다 — 없어진 메뉴를 든 옛 값이 화면에서 조용히 사라지지 않게. */
function MenuChips({ menus, keys }: { menus: ConsoleMenu[]; keys: string[] }): React.JSX.Element {
  if (keys.length === 0) return <span className="text-[11px] text-faint">없음</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {keys.map((key) => {
        const known = menus.find((menu) => menu.key === key);
        return (
          <Chip key={key} tone={known ? 'repo' : 'warn'}>
            {known?.label ?? key}
          </Chip>
        );
      })}
    </span>
  );
}
